import { useCallback, useEffect, useMemo, useState } from 'react';
import { css } from '@linaria/core';
import clsx from 'clsx';
import { Icon } from '@cbhq/cds-web/icons';
import { Box, BoxElement, HStack, VStack, VStackProps } from '@cbhq/cds-web/layout';
import { Spinner } from '@cbhq/cds-web/loaders/Spinner';
import {
  FullscreenModal,
  FullscreenModalProps,
} from '@cbhq/cds-web/overlays/Modal/FullscreenModal';
import { Pressable } from '@cbhq/cds-web/system';
import {
  Link,
  TextDisplay3,
  TextHeadline,
  TextTitle1,
  TextTitle2,
  TextTitle4,
} from '@cbhq/cds-web/typography';

import { useSimpleBreakpoints } from ':cloud/hooks/useSimpleBreakpoints';
import { EventName, logClick } from ':cloud/init/clientAnalytics/logging';
import { useGetBuildQuestQuestions } from ':cloud/queries/BuildQuestQueries/useGetBuildQuestQuestions';
import { useSubmitBuildQuestAnswer } from ':cloud/queries/BuildQuestQueries/useSubmitBuildQuestAnswer';
import { useGetUser } from ':cloud/queries/UserQueries/useGetUser';
import { BuildQuestItem } from ':cloud/types/service_proto';
import { generateStringValidationError } from ':cloud/utils/generateStringValidationError';
import { QuestionCopy } from ':cloud/widgets/buildQuest/types';
import { useOverrideFullscreenModalStyles } from ':cloud/widgets/buildQuest/useOverrideFullscreenModalStyles';

import { ReactComponent as StarIcon } from './assets/star.svg';
import questions from './questions';

const starIconBox = css`
  fill: var(--background-inverse);
`;

const spinnerLoader = css`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  justify-content: center;
  align-items: center;
`;

const inputWrapperBase = css`
  border-bottom: 3px solid;
  padding: 8px 0;
  width: 660px;
  border-color: var(--foreground);
`;
const inputWrapperSuccess = css`
  border-color: var(--green);
`;
const inputWrapperError = css`
  border-color: var(--orange);
`;
const inputWrapperMobile = css`
  width: 100%;
`;

const modalContainer = css`
  margin: 0;
  overflow: hidden;
  height: 100%;
  & .fsm-primary-content-container {
    padding: 0;
    max-width: unset;
    height: 100%;
  }
`;

const customInputClassBase = css`
  background-color: transparent;
  border: none;
  outline: none;
  color: var(--foreground);
  font-family: var(--display3-font-family);
  font-size: var(--display3-font-size);
  width: 100%;
`;

const customInputClassDesktop = css`
  font-family: var(--display3-font-family);
  font-size: var(--display3-font-size);
`;

const customInputClassMobile = css`
  font-family: var(--title3-font-family);
  font-size: var(--title3-font-size);
`;

function getCurrentQuestion(items: BuildQuestItem[], questionId: string) {
  return items.find((item) => item.questionId === questionId);
}

type SubmitButtonProps = {
  onSubmit: () => void;
  isLoading: boolean;
  isDisabled: boolean;
  block?: boolean;
};
function SubmitButton({ onSubmit, isLoading, isDisabled, block }: SubmitButtonProps) {
  return (
    <Pressable
      as="button"
      background="backgroundInverse"
      borderRadius="roundedFull"
      onPress={onSubmit}
      width={block ? '100%' : 150}
      disabled={isDisabled}
    >
      <Box
        spacingHorizontal={3}
        spacingVertical={2}
        justifyContent="center"
        alignItems="center"
        height={56}
      >
        {isLoading ? (
          <span className={spinnerLoader}>
            <Spinner size={2.5} />
          </span>
        ) : (
          <TextHeadline as="p" dangerouslySetColor="var(--background)">
            Submit
          </TextHeadline>
        )}
      </Box>
    </Pressable>
  );
}

type QuestionModalProps = Pick<FullscreenModalProps, 'visible' | 'onRequestClose' | 'onDidClose'> &
  Pick<BuildQuestItem, 'questionId'>;

export function QuestionModal({
  visible,
  onRequestClose,
  questionId,
  onDidClose,
}: QuestionModalProps): React.ReactElement {
  useOverrideFullscreenModalStyles({ visible, hideCloseButton: true });

  const { isDesktop, isPhone } = useSimpleBreakpoints();
  const { user } = useGetUser();
  const { items } = useGetBuildQuestQuestions(user.userId);

  // BE response for this questionId
  const currentQuestion = useMemo(() => getCurrentQuestion(items, questionId), [items, questionId]);
  const [error, setError] = useState('');

  const isQuestionComplete = useMemo(
    () => currentQuestion?.complete ?? false,
    [currentQuestion?.complete],
  );

  const isAnswerCorrect = useMemo(
    () => isQuestionComplete && currentQuestion?.pointValue === currentQuestion?.pointsAwarded,
    [currentQuestion?.pointsAwarded, currentQuestion?.pointValue, isQuestionComplete],
  );

  // FE content for this questionId
  const questionCopy: QuestionCopy = questions[questionId];
  const [showHint, setShowHint] = useState(false);
  const [answer, setAnswer] = useState<string>('');

  useEffect(() => {
    setAnswer(isAnswerCorrect ? currentQuestion?.userAnswer ?? '' : '');
  }, [isAnswerCorrect, currentQuestion?.userAnswer]);

  const handleOnChangeAnswer = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setError('');
    setAnswer(e.target.value);
  }, []);

  const handleShowHint = useCallback(() => {
    logClick(EventName.quest_question_hint_click, { questionId });
    setShowHint(true);
  }, [questionId]);

  const handleCloseModal = useCallback(() => {
    onRequestClose();
    setShowHint(false);
    setError('');
    setAnswer('');
  }, [onRequestClose]);

  const submitAnswerMutation = useSubmitBuildQuestAnswer();
  const handleSubmit = useCallback(() => {
    if (!questionCopy?.answerSchema) return;
    const stringError = generateStringValidationError(answer, questionCopy.answerSchema);
    if (stringError) {
      setError(stringError);
      return;
    }
    logClick(EventName.quest_question_submit_click, { questionId });
    submitAnswerMutation.mutate(
      {
        userId: user.userId,
        questionId,
        answer,
      },
      {
        onSuccess: (data) => {
          const { items: returnedItems } = data;
          const returnedItem = getCurrentQuestion(returnedItems, questionId);
          if (returnedItem?.userAnswer && !returnedItem?.complete) {
            setError("Hmm... that's not quite right. Please try again.");
          } else {
            setTimeout(() => {
              handleCloseModal();
            }, 1000);
          }
        },
      },
    );
  }, [
    submitAnswerMutation,
    user.userId,
    questionId,
    answer,
    questionCopy?.answerSchema,
    handleCloseModal,
  ]);

  const endContent = useMemo(() => {
    if (isQuestionComplete)
      return <Icon name="checkmark" size="l" dangerouslySetColor="var(--green)" />;
    if (!questionCopy?.hint) return null;
    return (
      <Box spacingHorizontal={2}>
        <Link variant="title1" color="primary" to="" onPress={handleShowHint}>
          Hint
        </Link>
      </Box>
    );
  }, [questionCopy?.hint, isQuestionComplete, handleShowHint]);

  const inputWrapper = clsx(inputWrapperBase, {
    [inputWrapperSuccess]: isQuestionComplete,
    [inputWrapperError]: !!error,
    [inputWrapperMobile]: !isDesktop,
  });

  const desktopWrapperProps: VStackProps<BoxElement> = {
    width: '100vw',
    height: 'calc(100vh - 80px)',
    alignItems: 'center',
    position: 'relative',
    top: 'calc(50% - 80px)',
    style: { transform: 'translateY(-25%)' },
  };

  const mobileWrapperProps: VStackProps<BoxElement> = {
    justifyContent: 'flex-start',
    height: '100%',
  };

  const primaryContent = (
    <VStack
      {...(isDesktop ? desktopWrapperProps : mobileWrapperProps)}
      gap={isDesktop ? 8 : 4}
      overflow="scroll"
    >
      <VStack gap={2} maxWidth={800} spacing={isDesktop ? 4 : 2} position="relative">
        <HStack height={80} alignItems="center">
          {isDesktop && (
            <Box position="absolute" top={40} left={-100} className={starIconBox}>
              <StarIcon />
            </Box>
          )}
          <HStack justifyContent="space-between" alignItems="flex-start" width="100%">
            <VStack>
              {Boolean(questionCopy?.title) &&
                (!isPhone ? (
                  <TextTitle1 as="h1">{questionCopy?.title}</TextTitle1>
                ) : (
                  <TextTitle2 as="p" color="foregroundMuted">
                    {questionCopy?.title}
                  </TextTitle2>
                ))}
              <TextTitle4 as="p" color="foregroundMuted">
                {currentQuestion?.pointValue} exp
              </TextTitle4>
            </VStack>
            <Pressable onPress={handleCloseModal} background="transparent" aria-label="Close">
              <Icon name="close" size="l" color="foreground" />
            </Pressable>
          </HStack>
        </HStack>

        {!isPhone ? (
          <TextDisplay3 as="h2">{questionCopy?.question}</TextDisplay3>
        ) : (
          <TextTitle1 as="h2">{questionCopy?.question}</TextTitle1>
        )}

        {/* description */}
        {!!questionCopy?.description && (
          <VStack spacingTop={isDesktop ? 4 : 2} as="ol">
            {questionCopy.description.map((item) => (
              <TextTitle4 as="li" key={item?.toString()} color="foregroundMuted">
                {item}
              </TextTitle4>
            ))}
          </VStack>
        )}

        {/* input and submit button */}
        {!isPhone ? (
          <>
            <HStack spacingTop={isDesktop ? 8 : 4} gap={isDesktop ? 7 : 4} alignItems="center">
              <Box className={inputWrapper} alignItems="center" spacingEnd={4}>
                <input
                  type="text"
                  className={clsx(customInputClassBase, customInputClassDesktop)}
                  placeholder={questionCopy?.placeholder}
                  value={answer}
                  onChange={handleOnChangeAnswer}
                  readOnly={isAnswerCorrect}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSubmit();
                    }
                  }}
                />
                {endContent}
              </Box>

              <SubmitButton
                onSubmit={handleSubmit}
                isLoading={submitAnswerMutation.isLoading}
                isDisabled={submitAnswerMutation.isLoading || isQuestionComplete}
              />
            </HStack>
            {error && (
              <TextTitle4 as="p" dangerouslySetColor="var(--orange)">
                {error}
              </TextTitle4>
            )}
            {showHint && (
              <TextHeadline as="p" dangerouslySetColor="var(--background-inverse)">
                {questionCopy?.hint}
              </TextHeadline>
            )}
          </>
        ) : (
          <VStack spacingTop={8} gap={3} alignItems="center">
            <VStack width="100%" gap={1}>
              <Box className={inputWrapper} alignItems="center" spacingEnd={4}>
                <input
                  type="text"
                  className={clsx(customInputClassBase, customInputClassMobile)}
                  placeholder={questionCopy?.placeholder}
                  value={answer}
                  onChange={handleOnChangeAnswer}
                  readOnly={isAnswerCorrect}
                />
                {endContent}
              </Box>
              {error && (
                <TextTitle4 as="p" dangerouslySetColor="var(--orange)">
                  {error}
                </TextTitle4>
              )}
              {showHint && (
                <TextHeadline as="p" dangerouslySetColor="var(--background-inverse)">
                  {questionCopy?.hint}
                </TextHeadline>
              )}
            </VStack>
          </VStack>
        )}
      </VStack>
      {isPhone && (
        <SubmitButton
          onSubmit={handleSubmit}
          isLoading={submitAnswerMutation.isLoading}
          isDisabled={submitAnswerMutation.isLoading || isQuestionComplete}
          block
        />
      )}
    </VStack>
  );

  return (
    <FullscreenModal
      hideDivider
      visible={visible}
      logo={<div />}
      dangerouslySetContentClassName={modalContainer}
      onRequestClose={handleCloseModal}
      primaryContent={primaryContent}
      closeAccessibilityLabel="Close"
      testID="custom-fullscreen-modal"
      onDidClose={onDidClose}
    />
  );
}
